import React, { useState } from 'react';
import { TextField, Popover, Box, IconButton, InputAdornment, Button as MuiButton } from '@mui/material';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import type { DateTime as Interface } from '../../design/components';
import { FontIcon } from './FontIcon';
import {
  type DatePickerVariant,
  getTimezoneAbbr,
  utcToLocal,
  localToUtc,
  formatDisplay,
  parseToDate,
  parseDateRange,
  formatDateISO,
  MONTH_NAMES_SHORT,
  getDecadeStart,
  buildDisabledDays,
  getStartMonth,
  getEndMonth,
  isMonthInRange,
  isYearInRange,
  minMaxToLocalTime,
} from '../shared/datetime';

export const DateTime: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder,
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
  variant = 'date',
  range,
  min,
  max,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const v = variant as DatePickerVariant;
  const strValue = typeof value === 'string' ? value : '';
  const showTz = v === 'date-time' || v === 'time';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const minStr = min != null ? String(min) : undefined;
  const maxStr = max != null ? String(max) : undefined;

  if (inline) {
    return (
      <Box component="span" sx={{ display: hidden ? 'none' : 'inline' }}>
        {formatDisplay(strValue, v, range)}
      </Box>
    );
  }

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!readonly && !disabled) setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  // Time range: two native time inputs
  if (range && v === 'time') {
    const [startStr, endStr] = strValue.split('/');
    const minTime = minMaxToLocalTime(min);
    const maxTime = minMaxToLocalTime(max);
    return (
      <Box sx={{ display: hidden ? 'none' : undefined }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            label={`${name} start`}
            type="time"
            value={utcToLocal(startStr || '', v)}
            onChange={(e) => {
              const start = localToUtc(e.target.value, v);
              setValue(endStr ? `${start}/${endStr}` : start);
            }}
            error={!!error}
            disabled={disabled}
            required={required}
            slotProps={{
              input: { readOnly: readonly },
              inputLabel: { shrink: true },
              htmlInput: { min: minTime, max: maxTime },
            }}
            sx={{ flex: 1 }}
          />
          <span>–</span>
          <TextField
            label={`${name} end`}
            type="time"
            value={utcToLocal(endStr || '', v)}
            onChange={(e) => {
              const end = localToUtc(e.target.value, v);
              setValue(startStr ? `${startStr}/${end}` : end);
            }}
            error={!!error}
            disabled={disabled}
            required={required}
            slotProps={{
              input: { readOnly: readonly },
              inputLabel: { shrink: true },
              htmlInput: { min: minTime, max: maxTime },
            }}
            sx={{ flex: 1 }}
          />
          <Box component="span" sx={{ color: 'text.secondary', fontSize: 13 }}>{getTimezoneAbbr()}</Box>
        </Box>
        {(error || description) && (
          <Box component="small" sx={{ color: error ? 'error.main' : 'text.secondary', display: 'block', mt: 0.5 }}>
            {error || description}
          </Box>
        )}
      </Box>
    );
  }

  // Month/year range: two popover pickers
  if (range && (v === 'month' || v === 'year')) {
    const [startStr, endStr] = strValue.split('/');
    return (
      <Box sx={{ display: hidden ? 'none' : undefined }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {v === 'month' ? (
            <>
              <MuiMonthPicker
                label={`${name} start`}
                value={startStr || ''}
                onChange={(val) => setValue(endStr ? `${val}/${endStr}` : val)}
                error={!!error} disabled={disabled} readonly={readonly}
                min={minStr} max={maxStr}
              />
              <span>–</span>
              <MuiMonthPicker
                label={`${name} end`}
                value={endStr || ''}
                onChange={(val) => setValue(startStr ? `${startStr}/${val}` : val)}
                error={!!error} disabled={disabled} readonly={readonly}
                min={minStr} max={maxStr}
              />
            </>
          ) : (
            <>
              <MuiYearPicker
                label={`${name} start`}
                value={startStr || ''}
                onChange={(val) => setValue(endStr ? `${val}/${endStr}` : val)}
                error={!!error} disabled={disabled} readonly={readonly}
                min={minStr} max={maxStr}
              />
              <span>–</span>
              <MuiYearPicker
                label={`${name} end`}
                value={endStr || ''}
                onChange={(val) => setValue(startStr ? `${startStr}/${val}` : val)}
                error={!!error} disabled={disabled} readonly={readonly}
                min={minStr} max={maxStr}
              />
            </>
          )}
        </Box>
        {(error || description) && (
          <Box component="small" sx={{ color: error ? 'error.main' : 'text.secondary', display: 'block', mt: 0.5 }}>
            {error || description}
          </Box>
        )}
      </Box>
    );
  }

  // Time single: native input
  if (v === 'time' && !range) {
    return (
      <Box sx={{ display: hidden ? 'none' : undefined }}>
        <TextField
          id={name}
          name={name}
          label={name}
          type="time"
          value={utcToLocal(strValue, v)}
          onChange={(e) => setValue(localToUtc(e.target.value, v))}
          placeholder={placeholder}
          error={!!error}
          disabled={disabled}
          required={required}
          fullWidth
          helperText={error || description || (showTz ? getTimezoneAbbr() : undefined)}
          slotProps={{
            input: { readOnly: readonly },
            inputLabel: { shrink: true },
            htmlInput: { min: minMaxToLocalTime(min), max: minMaxToLocalTime(max) },
          }}
        />
      </Box>
    );
  }

  // Month single: popover with month grid
  if (v === 'month' && !range) {
    return (
      <Box sx={{ display: hidden ? 'none' : undefined }}>
        <MuiMonthPicker
          label={name}
          value={strValue}
          onChange={setValue}
          error={!!error}
          disabled={disabled}
          readonly={readonly}
          placeholder={placeholder}
          helperText={error || description}
          required={required}
          fullWidth
          min={minStr}
          max={maxStr}
        />
      </Box>
    );
  }

  // Year single: popover with year grid
  if (v === 'year' && !range) {
    return (
      <Box sx={{ display: hidden ? 'none' : undefined }}>
        <MuiYearPicker
          label={name}
          value={strValue}
          onChange={setValue}
          error={!!error}
          disabled={disabled}
          readonly={readonly}
          placeholder={placeholder}
          helperText={error || description}
          required={required}
          fullWidth
          min={minStr}
          max={maxStr}
        />
      </Box>
    );
  }

  // Calendar-based: date, date-time (single or range)
  const displayValue = formatDisplay(strValue, v, range);
  const disabledDays = buildDisabledDays(min, max);
  const startMonth = getStartMonth(min);
  const endMonth = getEndMonth(max);

  return (
    <Box sx={{ display: hidden ? 'none' : undefined }}>
      <TextField
        id={name}
        name={name}
        label={name}
        value={displayValue}
        placeholder={placeholder || 'Select a date'}
        error={!!error}
        disabled={disabled}
        required={required}
        fullWidth
        helperText={error || description || (showTz ? getTimezoneAbbr() : undefined)}
        onClick={handleOpen}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {strValue && !readonly && !disabled ? (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setValue(''); }} aria-label="Clear date">
                    <FontIcon name="clear" size="small" />
                  </IconButton>
                ) : (
                  <FontIcon name="calendar_today" size="small" />
                )}
              </InputAdornment>
            ),
          },
        }}
        sx={{ cursor: readonly || disabled ? 'default' : 'pointer', '& .MuiInputBase-input': { cursor: readonly || disabled ? 'default' : 'pointer' } }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1 }}>
          {v === 'date' && !range && (
            <DayPicker
              mode="single"
              selected={parseToDate(strValue, v)}
              onSelect={(day) => { if (day) { setValue(formatDateISO(day)); handleClose(); } }}
              defaultMonth={parseToDate(strValue, v)}
              disabled={disabledDays}
              startMonth={startMonth}
              endMonth={endMonth}
            />
          )}
          {v === 'date' && range && (
            <DayPicker
              mode="range"
              selected={parseDateRange(strValue) || undefined}
              onSelect={(r) => {
                if (r?.from && r?.to) { setValue(`${formatDateISO(r.from)}/${formatDateISO(r.to)}`); }
              }}
              numberOfMonths={2}
              defaultMonth={parseDateRange(strValue)?.from}
              disabled={disabledDays}
              startMonth={startMonth}
              endMonth={endMonth}
            />
          )}
          {v === 'date-time' && !range && (
            <DateTimePicker value={strValue} onChange={(val) => { setValue(val); handleClose(); }} min={min} max={max} />
          )}
          {v === 'date-time' && range && (
            <DateTimeRangePicker value={strValue} onChange={setValue} min={min} max={max} />
          )}
        </Box>
      </Popover>
    </Box>
  );
};

/** Calendar + time input for single date-time */
function DateTimePicker({ value, onChange, min, max }: { value: string; onChange: (v: string) => void; min?: string | Date; max?: string | Date }) {
  const date = value ? new Date(value) : undefined;
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(date);
  const localTime = value ? utcToLocal(value, 'date-time').split('T')[1] || '12:00' : '12:00';
  const [time, setTime] = useState(localTime);
  const disabledDays = buildDisabledDays(min, max);

  const commit = (d: Date | undefined, t: string) => {
    if (!d) return;
    onChange(localToUtc(formatDateISO(d) + 'T' + t, 'date-time'));
  };

  return (
    <Box>
      <DayPicker
        mode="single"
        selected={selectedDate}
        onSelect={(day) => { setSelectedDate(day || undefined); if (day) commit(day, time); }}
        defaultMonth={selectedDate}
        disabled={disabledDays}
        startMonth={getStartMonth(min)}
        endMonth={getEndMonth(max)}
      />
      <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <input
          type="time"
          value={time}
          min={minMaxToLocalTime(min)}
          max={minMaxToLocalTime(max)}
          onChange={(e) => { setTime(e.target.value); commit(selectedDate, e.target.value); }}
          style={{ flex: 1, padding: '6px 8px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        <span style={{ color: '#666', fontSize: '13px' }}>{getTimezoneAbbr()}</span>
      </Box>
    </Box>
  );
}

/** Calendar range + two time inputs for date-time range */
function DateTimeRangePicker({ value, onChange, min, max }: { value: string; onChange: (v: string) => void; min?: string | Date; max?: string | Date }) {
  const [startStr, endStr] = value.split('/');
  const startDate = startStr ? new Date(startStr) : undefined;
  const endDate = endStr ? new Date(endStr) : undefined;
  const [range, setRange] = useState<{ from?: Date; to?: Date }>({
    from: startDate && !isNaN(startDate.getTime()) ? startDate : undefined,
    to: endDate && !isNaN(endDate.getTime()) ? endDate : undefined,
  });
  const startTime = startStr ? utcToLocal(startStr, 'date-time').split('T')[1] || '12:00' : '12:00';
  const endTime = endStr ? utcToLocal(endStr, 'date-time').split('T')[1] || '12:00' : '12:00';
  const [times, setTimes] = useState([startTime, endTime]);
  const disabledDays = buildDisabledDays(min, max);

  const commit = (r: { from?: Date; to?: Date }, t: string[]) => {
    if (!r.from || !r.to) return;
    const s = localToUtc(formatDateISO(r.from) + 'T' + t[0], 'date-time');
    const e = localToUtc(formatDateISO(r.to) + 'T' + t[1], 'date-time');
    onChange(`${s}/${e}`);
  };

  return (
    <Box>
      <DayPicker
        mode="range"
        selected={range.from && range.to ? { from: range.from, to: range.to } : undefined}
        onSelect={(r) => {
          const newRange = { from: r?.from || undefined, to: r?.to || undefined };
          setRange(newRange);
          if (newRange.from && newRange.to) commit(newRange, times);
        }}
        numberOfMonths={2}
        defaultMonth={range.from}
        disabled={disabledDays}
        startMonth={getStartMonth(min)}
        endMonth={getEndMonth(max)}
      />
      <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <input
          type="time"
          value={times[0]}
          onChange={(e) => { const t = [e.target.value, times[1]]; setTimes(t); commit(range, t); }}
          style={{ flex: 1, padding: '6px 8px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        <span>–</span>
        <input
          type="time"
          value={times[1]}
          onChange={(e) => { const t = [times[0], e.target.value]; setTimes(t); commit(range, t); }}
          style={{ flex: 1, padding: '6px 8px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        <span style={{ color: '#666', fontSize: '13px' }}>{getTimezoneAbbr()}</span>
      </Box>
    </Box>
  );
}

/** MUI Month picker — TextField that opens a Popover with 3×4 month grid */
function MuiMonthPicker({ label, value, onChange, error, disabled, readonly, placeholder, helperText, required, fullWidth, min, max }: {
  label?: string; value: string; onChange: (v: string) => void;
  error?: boolean; disabled?: boolean; readonly?: boolean; placeholder?: string; helperText?: string; required?: boolean; fullWidth?: boolean;
  min?: string; max?: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [y] = value ? value.split('-').map(Number) : [new Date().getFullYear()];
  const [year, setYear] = useState(y);

  return (
    <>
      <TextField
        label={label}
        value={value ? formatDisplay(value, 'month') : ''}
        placeholder={placeholder || 'Pick a month'}
        error={error}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
        helperText={helperText}
        onClick={(e) => { if (!readonly && !disabled) setAnchorEl(e.currentTarget); }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: value && !readonly && !disabled ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onChange(''); }}>
                  <FontIcon name="clear" size="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
        sx={{ flex: 1, cursor: readonly || disabled ? 'default' : 'pointer', '& .MuiInputBase-input': { cursor: readonly || disabled ? 'default' : 'pointer' } }}
      />
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2, width: 240 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <IconButton size="small" onClick={() => setYear(year - 1)}
              disabled={min ? year - 1 < Number(min.slice(0, 4)) : false}>
              <FontIcon name="chevron_left" size="small" />
            </IconButton>
            <Box sx={{ fontWeight: 500, fontSize: 14 }}>{year}</Box>
            <IconButton size="small" onClick={() => setYear(year + 1)}
              disabled={max ? year + 1 > Number(max.slice(0, 4)) : false}>
              <FontIcon name="chevron_right" size="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
            {MONTH_NAMES_SHORT.map((m, i) => {
              const monthVal = `${year}-${String(i + 1).padStart(2, '0')}`;
              const inRange = isMonthInRange(monthVal, min, max);
              return (
                <MuiButton
                  key={i}
                  size="small"
                  variant={monthVal === value ? 'contained' : 'text'}
                  disabled={!inRange}
                  onClick={() => { onChange(monthVal); setAnchorEl(null); }}
                  sx={{ minWidth: 0, textTransform: 'none' }}
                >
                  {m}
                </MuiButton>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}

/** MUI Year picker — TextField that opens a Popover with 3×4 year grid */
function MuiYearPicker({ label, value, onChange, error, disabled, readonly, placeholder, helperText, required, fullWidth, min, max }: {
  label?: string; value: string; onChange: (v: string) => void;
  error?: boolean; disabled?: boolean; readonly?: boolean; placeholder?: string; helperText?: string; required?: boolean; fullWidth?: boolean;
  min?: string; max?: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const currentYear = value ? Number(value) : new Date().getFullYear();
  const [decadeStart, setDecadeStart] = useState(getDecadeStart(currentYear));

  return (
    <>
      <TextField
        label={label}
        value={value || ''}
        placeholder={placeholder || 'Pick a year'}
        error={error}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
        helperText={helperText}
        onClick={(e) => { if (!readonly && !disabled) setAnchorEl(e.currentTarget); }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: value && !readonly && !disabled ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onChange(''); }}>
                  <FontIcon name="clear" size="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
        sx={{ flex: 1, cursor: readonly || disabled ? 'default' : 'pointer', '& .MuiInputBase-input': { cursor: readonly || disabled ? 'default' : 'pointer' } }}
      />
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2, width: 240 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <IconButton size="small" onClick={() => setDecadeStart(decadeStart - 12)}
              disabled={min ? decadeStart - 1 < Number(min.slice(0, 4)) : false}>
              <FontIcon name="chevron_left" size="small" />
            </IconButton>
            <Box sx={{ fontWeight: 500, fontSize: 14 }}>{decadeStart}–{decadeStart + 11}</Box>
            <IconButton size="small" onClick={() => setDecadeStart(decadeStart + 12)}
              disabled={max ? decadeStart + 12 > Number(max.slice(0, 4)) : false}>
              <FontIcon name="chevron_right" size="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
            {Array.from({ length: 12 }, (_, i) => {
              const yr = String(decadeStart + i);
              const inRange = isYearInRange(yr, min, max);
              return (
                <MuiButton
                  key={yr}
                  size="small"
                  variant={yr === value ? 'contained' : 'text'}
                  disabled={!inRange}
                  onClick={() => { onChange(yr); setAnchorEl(null); }}
                  sx={{ minWidth: 0 }}
                >
                  {yr}
                </MuiButton>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
